import {FC} from 'react';
import {FieldValues, useFormContext, UseFormRegister} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import clsx from 'clsx';
import Address from 'components/Address';
import Button from 'components/Button';
import useBreakpoint from 'hooks/useBreakpoint';
import {Option} from 'types';
import {AddressType} from 'types/address';
import {PartialFormData} from '../../types';
import ChangeAddressFormat from './ChangeAddressFormat';

export type SenderInformationProps = PartialFormData & {
    contactName: string;
    onDelete: () => void;
    onEdit: (targetAddress: 'signupAddress' | 'partnerAddress') => void;
    onFormatChange: (targetAddress: 'signupAddress' | 'partnerAddress') => void;
    prefectures: Array<Option>;
};

const name = 'senderInformation';

type SenderInformationContactProps = {
    address: Partial<AddressType>;
    contactName: string;
    faxNumber?: string;
    hasError: boolean;
    isJapaneseFormat: boolean;
    label: string;
    onDelete: () => void;
    onEdit: () => void;
    onFormatChange: () => void;
    phoneNumber?: string;
    register: UseFormRegister<FieldValues>;
    selected: boolean;
    value: string;
};

const SenderInformationContact: FC<SenderInformationContactProps> = ({
    address,
    contactName,
    faxNumber,
    hasError,
    isJapaneseFormat,
    label,
    onDelete,
    onEdit,
    onFormatChange,
    phoneNumber,
    register,
    selected,
    value,
}) => {
    const {t} = useTranslation('Profile.Setup.Preview');
    const isBreeakpointMd = useBreakpoint('md');

    return (
        <label
            className={clsx(
                'cursor-pointer rounded border ',
                selected ? 'border-blue-100 bg-blue-50' : 'border-grey-200',
                hasError && 'ring-2 ring-coral-100',
            )}
            htmlFor={`${name}-${label}`}
        >
            <div>
                <div className="flex flex-none flex-col-reverse p-2 md:flex-row">
                    <div className="flex items-center">
                        <input
                            className="ml-1"
                            {...register(name, {required: true})}
                            id={`${name}-${label}`}
                            required={hasError}
                            type="radio"
                            value={value}
                        />
                        <div className="ml-2" role="gridcell">
                            {t('senderInformation.businessNameAndThisContact')}
                        </div>
                    </div>
                    <div className="flex flex-1 items-start justify-end gap-1 pb-2 pl-2 md:pb-0">
                        {value === 'partnerAddress' && (
                            <Button
                                aria-label={`delete-${label}`}
                                className="flex-1 md:flex-none"
                                icon={isBreeakpointMd ? 'trash' : undefined}
                                kind="destructive"
                                onClick={() => onDelete()}
                                size="xs"
                            >
                                {t('senderInformation.delete')}
                            </Button>
                        )}
                        <Button
                            aria-label={`edit-${label}`}
                            className="flex-1 md:flex-none"
                            icon={isBreeakpointMd ? 'edit' : undefined}
                            kind="secondary"
                            onClick={() => onEdit()}
                            size="xs"
                        >
                            {t('senderInformation.edit')}
                        </Button>
                    </div>
                </div>
                <div className="px-4 pb-4">
                    <span className="font-semibold">{contactName}</span>
                    <Address address={address} aria-label={label} />
                    {phoneNumber && (
                        <div>
                            {t('senderInformation.telephone', {
                                telephone: phoneNumber,
                            })}
                        </div>
                    )}
                    {faxNumber && (
                        <div>
                            {t('senderInformation.fax', {
                                fax: faxNumber,
                            })}
                        </div>
                    )}
                </div>
            </div>
            {isJapaneseFormat && (
                <ChangeAddressFormat
                    format={address.addressFormat}
                    onClick={() => onFormatChange()}
                    selected={selected}
                />
            )}
        </label>
    );
};

type SenderInformationBusinessNameProps = {
    hasError: boolean;
    register: UseFormRegister<FieldValues>;
    selected: boolean;
};

const SenderInformationBusinessName: FC<SenderInformationBusinessNameProps> = ({
    hasError,
    register,
    selected,
}) => {
    const {t} = useTranslation('Profile.Setup.Preview');

    return (
        <label
            className={clsx(
                'grid cursor-pointer grid-cols-12 rounded border border-grey-200 p-2',
                selected ? 'border-blue-100 bg-blue-50' : 'border-grey-200',
                hasError && 'ring-2 ring-coral-100',
            )}
            htmlFor={`${name}-businessNameOnly`}
        >
            <div role="gridcell">
                <input
                    className="ml-1"
                    {...register(name, {required: true})}
                    id={`${name}-businessNameOnly`}
                    required={hasError}
                    type="radio"
                    value="businessNameOnly"
                />
            </div>
            <div className="col-span-11" role="gridcell">
                {t('senderInformation.businessNameOnly')}
            </div>
        </label>
    );
};

const SenderInformation = ({
    addresses,
    contactName,
    onDelete,
    onEdit,
    onFormatChange,
    partnerFaxNumber,
    partnerPhoneNumber,
    prefectures,
    signupFaxNumber,
    signupPhoneNumber,
}: SenderInformationProps) => {
    const {register} = useFormContext();

    const {
        formState: {errors},
        getValues,
        watch,
    } = useFormContext();

    const selection = watch(name);
    const signupSelected = selection === 'signupAddress';
    const partnerSelected = selection === 'partnerAddress';

    return (
        <>
            {addresses.signupAddress !== undefined && (
                <SenderInformationContact
                    address={{
                        addressFormat: addresses.signupAddress.addressFormat,
                        countryName: addresses.signupAddress.country,
                        extendedAddress:
                            addresses.signupAddress.extendedAddress || '',
                        locality: addresses.signupAddress.locality,
                        postalCode: addresses.signupAddress.postalCode || '',
                        region:
                            (prefectures.find(
                                (option) =>
                                    option.value ===
                                    addresses.signupAddress.region,
                            )?.label as string) ||
                            addresses.signupAddress.region,
                        streetAddress: addresses.signupAddress.streetAddress,
                    }}
                    contactName={contactName}
                    faxNumber={signupFaxNumber}
                    hasError={!!errors[name]}
                    isJapaneseFormat={addresses.signupAddress.country === 'JP'}
                    label="signup-address"
                    onDelete={() => onDelete()}
                    onEdit={() => onEdit('signupAddress')}
                    onFormatChange={() => onFormatChange('signupAddress')}
                    phoneNumber={signupPhoneNumber}
                    register={register}
                    selected={signupSelected}
                    value="signupAddress"
                />
            )}
            {addresses.partnerAddress !== undefined && (
                <SenderInformationContact
                    address={{
                        addressFormat: addresses.partnerAddress.addressFormat,
                        countryName: addresses.partnerAddress.country,
                        extendedAddress:
                            addresses.partnerAddress.extendedAddress || '',
                        locality: addresses.partnerAddress.locality,
                        postalCode: addresses.partnerAddress.postalCode || '',
                        region:
                            (prefectures.find(
                                (option) =>
                                    option.value ===
                                    addresses.partnerAddress?.region,
                            )?.label as string) ||
                            addresses.partnerAddress.region,
                        streetAddress: addresses.partnerAddress.streetAddress,
                    }}
                    contactName={contactName}
                    faxNumber={partnerFaxNumber}
                    hasError={!!errors[name]}
                    isJapaneseFormat={addresses.partnerAddress.country === 'JP'}
                    label="partner-address"
                    onDelete={() => onDelete()}
                    onEdit={() => onEdit('partnerAddress')}
                    onFormatChange={() => onFormatChange('partnerAddress')}
                    phoneNumber={partnerPhoneNumber}
                    register={register}
                    selected={partnerSelected}
                    value="partnerAddress"
                />
            )}
            <SenderInformationBusinessName
                hasError={!!errors[name]}
                register={register}
                selected={getValues(name) === 'businessNameOnly'}
            />
        </>
    );
};

export default SenderInformation;
